import { AnalyticsProvider } from '~/utils/analytics-provider';

/**
 * Microsoft Clarity Analytics Provider
 * Professional implementation following provider interface
 */
export default class MicrosoftClarityProvider extends AnalyticsProvider {
  constructor() {
    super('Microsoft Clarity');
    this.clarityLoaded = false;
  }

  /**
   * Initialize Microsoft Clarity
   */
  initialize(config, propertyContext) {
    super.initialize(config, propertyContext);

    if (typeof window === 'undefined') {
      this.error('Cannot initialize Microsoft Clarity in SSR environment');
      return;
    }

    this.loadClarityScript();
    this.setPropertyContext();
  }

  /**
   * Load Microsoft Clarity script
   */
  loadClarityScript() {
    if (window.clarity) {
      this.clarityLoaded = true;
      return;
    }

    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", this.config.project_id);

    this.clarityLoaded = true;
    this.log('Script loaded successfully');
  }

  /**
   * Set property context for Microsoft Clarity
   */
  setPropertyContext() {
    if (!window.clarity || !this.propertyContext) return;

    try {
      window.clarity('set', 'property_name', this.propertyContext.property_name);
      window.clarity('set', 'property_brand', this.propertyContext.property_brand);
      window.clarity('set', 'property_location', `${this.propertyContext.location?.city}, ${this.propertyContext.location?.country}`);
    } catch (error) {
      this.error('Failed to set property context:', error);
    }
  }

  /**
   * Track events using Microsoft Clarity
   */
  track(eventName, eventData) {
    if (!this.isReady() || !window.clarity) {
      this.error('Provider not ready or clarity not available');
      return;
    }

    const clarityEvent = this.mapEvent(eventName);
    const clarityData = this.formatEventData(eventName, eventData);

    try {
      if (clarityData) {
        window.clarity('event', clarityEvent, clarityData);
      } else {
        window.clarity('event', clarityEvent);
      }

      this.log(`Event tracked: ${clarityEvent}`, clarityData);
    } catch (error) {
      this.error('Failed to track event:', error);
    }
  }

  /**
   * Map standard events to Microsoft Clarity events
   */
  mapEvent(standardEvent) {
    const clarityEventMap = {
      'page_view': 'page_view',
      'view_item': 'view_accommodation',
      'add_to_cart': 'add_to_cart',
      'remove_from_cart': 'remove_from_cart',
      'begin_checkout': 'begin_checkout',
      'add_payment_info': 'add_payment_info',
      'purchase': 'booking_completed',
      'search': 'search_accommodation',
      'view_item_list': 'view_room_list'
    };

    return clarityEventMap[standardEvent] || standardEvent;
  }

  /**
   * Format event data for Microsoft Clarity
   */
  formatEventData(eventName, eventData) {
    const ecommerce = this.extractEcommerceData(eventData);

    switch (eventName) {
      case 'view_item':
      case 'add_to_cart':
      case 'remove_from_cart':
        return {
          property_name: this.propertyContext?.property_name,
          item_ids: ecommerce.items?.map(item => item.item_id) || [],
          value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR'
        };

      case 'begin_checkout':
        return {
          property_name: this.propertyContext?.property_name,
          cart_value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR',
          item_count: ecommerce.items?.length || 0
        };

      case 'purchase':
        return {
          property_name: this.propertyContext?.property_name,
          booking_value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR',
          room_count: ecommerce.items?.length || 0
        };

      case 'search':
        return {
          search_term: eventData.search_term || '',
          property_name: this.propertyContext?.property_name
        };

      default:
        return {
          property_name: this.propertyContext?.property_name
        };
    }
  }

  /**
   * Get provider type for consent checking
   */
  getProviderType() {
    return 'experience'; // Microsoft Clarity is for user experience analysis
  }

  /**
   * Check if provider is ready
   */
  isReady() {
    return super.isReady() && this.clarityLoaded && window.clarity;
  }
}